import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, Receipt, Repeat, Trash2, Pencil, Lock, Package, X,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';

const fmt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;
const METHODS = [['cash', 'Наличные'], ['card', 'Карта'], ['kaspi', 'Kaspi'], ['transfer', 'Перевод'], ['bank', 'Банк']];
const SOURCE_LABELS = { manual: 'Вручную', recurring: 'Периодический', inventory_purchase: 'Из склада' };

const emptyForm = { category: 'Аренда', amount: '', occurred_on: new Date().toISOString().slice(0, 10), branch_id: 'all', counterparty: '', method: 'transfer', note: '' };

function AdminFinanceExpenses({ branchId, branches }) {
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const [showRecurring, setShowRecurring] = useState(false);
  const [recurring, setRecurring] = useState([]);
  const [recForm, setRecForm] = useState({ category: 'Аренда', amount: '', day_of_month: 1, branch_id: 'all', counterparty: '', method: 'transfer', note: '' });

  useEffect(() => {
    axios.get(`${API}/finance/expense-categories`, { headers: getAuthHeaders() })
      .then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ branch_id: branchId, category, source });
      if (search) p.set('search', search);
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      const r = await axios.get(`${API}/finance/expenses?${p}`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch { toast.error('Не удалось загрузить расходы'); }
    finally { setLoading(false); }
  }, [branchId, category, source, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const loadRecurring = async () => {
    try {
      const r = await axios.get(`${API}/finance/recurring`, { headers: getAuthHeaders() });
      setRecurring(r.data.recurring || []);
    } catch { toast.error('Ошибка'); }
  };

  const openCreate = () => { setForm({ ...emptyForm, branch_id: branchId }); setEditingId(null); setShowForm(true); };
  const openEdit = (e) => {
    setForm({ category: e.category, amount: e.amount, occurred_on: e.occurred_on, branch_id: e.branch_id || 'all', counterparty: e.counterparty || '', method: e.method || 'transfer', note: e.note || '' });
    setEditingId(e.id); setShowForm(true);
  };

  const saveExpense = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Укажите сумму'); return; }
    try {
      if (editingId) await axios.patch(`${API}/finance/expenses/${editingId}`, form, { headers: getAuthHeaders() });
      else await axios.post(`${API}/finance/expenses`, form, { headers: getAuthHeaders() });
      toast.success(editingId ? 'Расход обновлён' : 'Расход добавлен');
      setShowForm(false); load();
    } catch { toast.error('Ошибка при сохранении'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Удалить расход?')) return;
    try { await axios.delete(`${API}/finance/expenses/${id}`, { headers: getAuthHeaders() }); toast.success('Удалено'); load(); }
    catch { toast.error('Ошибка'); }
  };

  const saveRecurring = async () => {
    if (!recForm.amount || Number(recForm.amount) <= 0) { toast.error('Укажите сумму'); return; }
    try { await axios.post(`${API}/finance/recurring`, recForm, { headers: getAuthHeaders() }); toast.success('Шаблон добавлен'); setRecForm({ ...recForm, amount: '', counterparty: '', note: '' }); loadRecurring(); }
    catch { toast.error('Ошибка'); }
  };
  const toggleRecurring = async (r) => {
    try { await axios.patch(`${API}/finance/recurring/${r.id}`, { is_active: !r.is_active }, { headers: getAuthHeaders() }); loadRecurring(); } catch { toast.error('Ошибка'); }
  };
  const deleteRecurring = async (id) => {
    if (!window.confirm('Удалить шаблон?')) return;
    try { await axios.delete(`${API}/finance/recurring/${id}`, { headers: getAuthHeaders() }); loadRecurring(); } catch { toast.error('Ошибка'); }
  };

  const openRecurring = () => { setShowRecurring(true); loadRecurring(); };
  const sum = data?.summary;

  return (
    <div data-testid="finance-expenses-tab">
      {/* Summary */}
      {sum && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Card data-testid="exp-sum-total"><CardContent className="p-4">
            <div className="text-xs text-gray-500">Расходы за период</div>
            <div className="text-2xl font-bold text-gray-900">{fmt(sum.total)}</div>
            <div className="text-[11px] text-gray-400">{sum.count} операций · {sum.date_from} — {sum.date_to}</div>
          </CardContent></Card>
          {sum.by_category.slice(0, 3).map((c) => (
            <Card key={c.category}><CardContent className="p-4">
              <div className="text-xs text-gray-500 line-clamp-1">{c.category}</div>
              <div className="text-xl font-bold text-gray-900">{fmt(c.amount)}</div>
              <div className="text-[11px] text-gray-400">{sum.total ? Math.round(c.amount / sum.total * 100) : 0}% от расходов</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input placeholder="Поставщик или заметка" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} className="pl-8 w-56" data-testid="exp-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[170px]" data-testid="exp-category-filter"><SelectValue placeholder="Категория" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все категории</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[150px]" data-testid="exp-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все источники</SelectItem>
            <SelectItem value="manual">Вручную</SelectItem>
            <SelectItem value="recurring">Периодические</SelectItem>
            <SelectItem value="inventory_purchase">Из склада</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" data-testid="exp-date-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" data-testid="exp-date-to" />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={openRecurring} data-testid="exp-recurring-btn"><Repeat className="w-4 h-4 mr-1" /> Периодические</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openCreate} data-testid="exp-add-btn"><Plus className="w-4 h-4 mr-1" /> Добавить расход</Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400" data-testid="exp-loading">Загрузка…</div>
      ) : (
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Дата</th>
                <th className="text-left px-4 py-2">Категория</th>
                <th className="text-left px-4 py-2">Контрагент</th>
                <th className="text-left px-4 py-2">Филиал</th>
                <th className="text-left px-4 py-2">Источник</th>
                <th className="text-right px-4 py-2">Сумма</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data?.expenses.map((e, i) => (
                <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`expense-row-${i}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{e.occurred_on}</td>
                  <td className="px-4 py-2">{e.category}</td>
                  <td className="px-4 py-2 text-gray-600">{e.counterparty || '—'}{e.note && <span className="block text-[11px] text-gray-400">{e.note}</span>}</td>
                  <td className="px-4 py-2 text-gray-500">{e.branch_name}</td>
                  <td className="px-4 py-2">
                    <Badge variant={e.source === 'inventory_purchase' ? 'secondary' : 'outline'} className="text-[10px] flex items-center gap-1 w-fit">
                      {e.source === 'inventory_purchase' && <Package className="w-3 h-3" />}
                      {e.source === 'recurring' && <Repeat className="w-3 h-3" />}
                      {SOURCE_LABELS[e.source] || e.source}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right font-bold whitespace-nowrap">{fmt(e.amount)}</td>
                  <td className="px-4 py-2 text-right">
                    {e.editable ? (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(e)} className="text-gray-400 hover:text-emerald-600" data-testid={`exp-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteExpense(e.id)} className="text-gray-400 hover:text-red-600" data-testid={`exp-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ) : <Lock className="w-3.5 h-3.5 text-gray-300 ml-auto" title="Из склада — редактируется в инвентаре" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data || data.expenses.length === 0) && <div className="text-center text-gray-400 py-12">Нет расходов за выбранный период</div>}
        </CardContent></Card>
      )}

      {/* Add/Edit expense dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="expense-form-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Редактировать расход' : 'Новый расход'}</DialogTitle>
            <DialogDescription>Укажите категорию, сумму, дату и филиал расхода.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Категория</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Сумма, ₸</Label>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Дата</Label>
                <Input type="date" value={form.occurred_on} onChange={e => setForm({ ...form, occurred_on: e.target.value })} data-testid="form-date" />
              </div>
              <div>
                <Label className="text-xs">Филиал</Label>
                <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="form-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Общий</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Контрагент</Label>
                <Input value={form.counterparty} onChange={e => setForm({ ...form, counterparty: e.target.value })} data-testid="form-counterparty" />
              </div>
              <div>
                <Label className="text-xs">Способ оплаты</Label>
                <Select value={form.method} onValueChange={v => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="form-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Заметка</Label>
              <Textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="form-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Отмена</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveExpense} data-testid="form-save-btn">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring templates dialog */}
      <Dialog open={showRecurring} onOpenChange={setShowRecurring}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recurring-modal">
          <DialogHeader>
            <DialogTitle>Периодические расходы</DialogTitle>
            <DialogDescription>Шаблоны (аренда, зарплата и т.п.) — система сама создаёт расход каждый месяц в указанный день.</DialogDescription>
          </DialogHeader>
          {/* Add template */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-gray-50 p-3 rounded-lg">
            <Select value={recForm.category} onValueChange={v => setRecForm({ ...recForm, category: v })}>
              <SelectTrigger data-testid="rec-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Сумма ₸" value={recForm.amount} onChange={e => setRecForm({ ...recForm, amount: e.target.value })} data-testid="rec-amount" />
            <Input type="number" min="1" max="28" placeholder="День (1-28)" value={recForm.day_of_month} onChange={e => setRecForm({ ...recForm, day_of_month: e.target.value })} data-testid="rec-day" />
            <Select value={recForm.branch_id} onValueChange={v => setRecForm({ ...recForm, branch_id: v })}>
              <SelectTrigger data-testid="rec-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Общий</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Контрагент" value={recForm.counterparty} onChange={e => setRecForm({ ...recForm, counterparty: e.target.value })} data-testid="rec-counterparty" />
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveRecurring} data-testid="rec-add-btn"><Plus className="w-4 h-4 mr-1" /> Добавить</Button>
          </div>
          {/* List */}
          <div className="space-y-2 mt-3" data-testid="recurring-list">
            {recurring.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Шаблонов пока нет</p>}
            {recurring.map(r => (
              <div key={r.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`rec-item-${r.id}`}>
                <div>
                  <div className="font-medium text-sm">{r.category} · {fmt(r.amount)}<span className="text-gray-400 font-normal"> / мес (день {r.day_of_month})</span></div>
                  <div className="text-xs text-gray-500">{r.branch_name} · {r.counterparty || '—'}{r.last_generated_ym && ` · последний: ${r.last_generated_ym}`}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={r.is_active} onCheckedChange={() => toggleRecurring(r)} data-testid={`rec-toggle-${r.id}`} />
                  <button onClick={() => deleteRecurring(r.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminFinanceExpenses;
